import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, MessageCircle, DollarSign, Zap, Users, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Startup = () => {
  const differentiators = [
    {
      Icon: CheckCircle,
      title: 'Decision Clarity',
      description: 'We help you figure out what to build, what NOT to build, and why. No confusing jargon, just clear explanations that help you make informed decisions.',
    },
    {
      Icon: Zap,
      title: 'Avoiding Rebuilds',
      description: 'Most startups waste 6-12 months rebuilding systems that were rushed the first time. We help you get it right from the start: fast, but durable.',
    },
    {
      Icon: ArrowRight,
      title: 'Shipping Responsibly Under Pressure',
      description: 'We understand startup urgency. But urgency doesn\'t mean cutting corners. It means ruthless prioritization and building only what matters.',
    },
    {
      Icon: MessageCircle,
      title: 'Plain English Communication',
      description: 'We explain everything in terms you understand. You stay in control of your business decisions while we handle the technical execution.',
    },
    {
      Icon: Users,
      title: 'Direct Access, No Runaround',
      description: 'You work directly with the people writing your code. No account managers, no layers of bureaucracy, no waiting days for answers.',
    },
    {
      Icon: DollarSign,
      title: 'Transparent, Bounded Pricing',
      description: 'Fixed packages with clear scope. You know the cost upfront: initial payment to start, remaining balance due upon completion.',
    },
  ];

  const steps = [
    {
      step: '1',
      title: 'Discovery Session',
      description: 'Free 2-hour session to scope your project. We\'ll understand your constraints, timeline, and what success looks like.',
    },
    {
      step: '2',
      title: 'Technical Blueprint',
      description: 'We deliver a complete technical blueprint: architecture, database design, infrastructure plan, cost estimates, and phased implementation roadmap. You make informed decisions, not blind bets.',
    },
    {
      step: '3',
      title: 'Choose Your Next Step',
      description: 'Take the blueprint to any dev shop, add ongoing advisory support while you build with your team, or hire us to build the full MVP. Complete flexibility: you choose what fits your needs and budget.',
      cta: true,
    },
    {
      step: '4',
      title: 'Build & Ship (Optional)',
      description: 'If you choose the full build option: We build in weekly sprints with regular check-ins. Direct access to developers, no account managers, no runaround. You own the code.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>For Startups & Founders — BrightWings</title>
        <meta name="description" content="Decision clarity for founders who refuse to build blind. Whether you need a roadmap, advisory, or a full build — we meet you where you are." />
        <link rel="canonical" href="https://brightwings.io/startups" />
        <meta property="og:url" content="https://brightwings.io/startups" />
        <meta property="og:title" content="For Startups & Founders — BrightWings" />
        <meta property="og:description" content="Decision clarity for founders who refuse to build blind. Whether you need a roadmap, advisory, or a full build — we meet you where you are." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="For Startups & Founders — BrightWings" />
        <meta name="twitter:description" content="Decision clarity for founders who refuse to build blind. Whether you need a roadmap, advisory, or a full build — we meet you where you are." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">For Startups & Founders</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Decision clarity for founders who refuse to <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>build blind.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '560px', margin: '0 auto 32px' }}>
              We help founders bring decision clarity to software development. Whether you need a roadmap, ongoing advisory, or a full build: we meet you where you are.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
                Start a conversation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="bw-stats-bar">
        <div className="max-w-4xl mx-auto w-full flex flex-wrap justify-center">
          {[
            { val: '15+', lbl: 'Years building startups' },
            { val: '2 wks', lbl: 'Blueprint delivery' },
            { val: '$10K', lbl: 'Avg MVP value' },
          ].map((s, i) => (
            <div key={i} className="bw-stat-item">
              <div className="bw-stat-val">{s.val}</div>
              <div className="bw-stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Problem */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl">
            <span className="bw-eyebrow">The Reality</span>
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 40px)', marginBottom: '20px' }}>
              Most founders come to us after a painful experience.
            </h2>
            <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.7, marginBottom: '32px' }}>
              A developer who disappeared. A scope that ballooned. A product that got built but didn't solve the right thing. Speed without clarity isn't startup energy — it's expensive drift.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  label: "What's happening",
                  body: "Proposals don't make sense. Developers are hard to evaluate. Every decision feels like a potential mistake.",
                },
                {
                  label: 'How it feels',
                  body: "The vision is slipping out of your hands. You're dependent on whoever is holding the code — and it's frightening.",
                },
                {
                  label: "What's at stake",
                  body: "Startups that build without direction don't just waste money — they waste the window. Runway is finite. Rebuilds are expensive.",
                },
              ].map((item, i) => (
                <div key={i} style={{ borderLeft: '3px solid var(--bw-blue)', paddingLeft: '20px' }}>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>{item.label}</p>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Direction Beats Speed */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '16px' }}>Why Direction Beats Speed</h2>
            <p style={{ fontSize: '20px', color: 'var(--bw-dark)', fontWeight: 500, marginBottom: '40px' }}>
              Trust is not a feeling. It is a multiplier.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  title: 'You ship faster when you trust the plan',
                  body: 'Less second-guessing. Fewer rewrites. More momentum when it matters most. Direction removes the friction that slows startups down.',
                },
                {
                  title: 'You make better calls under pressure',
                  body: 'Limited runway, aggressive timelines, changing requirements — we\'ve been here before. Ruthless prioritization is a skill, not a personality trait.',
                },
                {
                  title: 'You don\'t rebuild what you already built',
                  body: 'Speed without restraint creates technical debt that costs you later. We build fast, but we build things that can survive your growth.',
                },
              ].map((item, i) => (
                <div key={i} style={{ borderLeft: '3px solid var(--bw-blue)', paddingLeft: '20px' }}>
                  <h3 style={{ ...hStyle, fontSize: '17px', marginBottom: '10px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '12px' }}>What Makes Us Different</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {differentiators.map((item, i) => (
              <div key={i} className="bw-feature-card">
                <div className="bw-feature-icon">
                  <item.Icon size={20} color="var(--bw-blue)" />
                </div>
                <h3 style={{ ...hStyle, fontSize: '17px', marginBottom: '10px' }}>{item.title}</h3>
                <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '14px' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '40px' }}>Startup Pricing</h2>
          <div className="max-w-2xl">
            <div className="bw-card" style={{ border: '2px solid var(--bw-blue)' }}>
              <div className="bw-card-header">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--bw-blue)' }} />
                  <span className="bw-eyebrow" style={{ marginBottom: 0 }}>Standard Pricing</span>
                </div>
                <h3 style={{ ...hStyle, fontSize: '28px', marginBottom: '8px' }}>Packages start at $3–5K</h3>
                <p style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>Most MVPs around $10K. All builds begin with a Technical Blueprint to ensure scope clarity.</p>
              </div>
              <div className="bw-card-body">
                <ul className="space-y-3">
                  {[
                    'Fixed scope, transparent pricing',
                    'Initial payment upfront',
                    'Remaining balance due upon completion',
                    'Weekly progress updates',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={17} color="var(--bw-blue)" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span style={{ color: 'var(--bw-text-mid)', fontSize: '15px' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--bw-muted)' }}>
              Square One alumni?{' '}
              <Link to="/squareone-alumni" onClick={() => window.scrollTo(0, 0)} style={{ color: 'var(--bw-blue)' }} className="underline hover:no-underline">
                Ask about discounts.
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)', marginBottom: '48px' }}>How We Work With Startups</h2>
          <div className="space-y-8">
            {steps.map((item, i) => (
              <div key={i} className="flex items-start gap-6">
                <div className="bw-step-circle flex-shrink-0" style={{ width: '52px', height: '52px', background: 'white', border: '2px solid var(--bw-blue)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '18px', color: 'var(--bw-blue)', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                  {item.step}
                </div>
                <div className="flex-1 pb-8" style={{ borderBottom: i < steps.length - 1 ? '1px solid var(--bw-border)' : 'none' }}>
                  <h3 style={{ ...hStyle, fontSize: '20px', marginBottom: '10px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '16px' }}>{item.description}</p>
                  {item.cta && (
                    <Link
                      to="/contact"
                      onClick={() => window.scrollTo(0, 0)}
                      className="inline-flex items-center gap-2 font-semibold mt-4"
                      style={{ color: 'var(--bw-blue)', fontSize: '15px' }}
                    >
                      Start a conversation
                      <ArrowRight size={16} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakes */}
      <section className="bw-section-surface">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(22px, 3vw, 32px)', marginBottom: '20px' }}>
            The window is finite.
          </h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', lineHeight: 1.7, marginBottom: '20px' }}>
            Most startups don't fail because the idea was wrong. They fail because they burned their runway figuring out what to build — and ran out of time before they got it right.
          </p>
          <p style={{ fontSize: '18px', color: 'var(--bw-dark)', fontWeight: 600, lineHeight: 1.7 }}>
            A technical blueprint costs weeks. A rebuild costs months. The gap between those two is often the company.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>Build with direction. Ship with confidence.</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Start with clarity. Get a complete Technical Blueprint that defines your architecture, scope, and implementation roadmap before committing to a full build.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Start a conversation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Startup;
